import React from "react";
import { useState, useEffect } from "react";
import { api } from "../../api/client";
import { formatKarachiDate } from "../../utils/dateUtils";

const TabPayments = () => {
  const [paymentTab, setPaymentTab] = useState("overview");
  const [passengers, setPassengers] = useState([]);
  const [selectedPassengerId, setSelectedPassengerId] = useState(null);
  const [passengerHistory, setPassengerHistory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [createForm, setCreateForm] = useState({
    passengerId: "",
    amount: "",
    dueDate: "",
  });
  const [bulkSelectedPayments, setBulkSelectedPayments] = useState([]);

  const loadPassengers = async () => {
    try {
      const res = await api("/admin/payments/all-passengers");
      setPassengers(res.passengers);
    } catch (err) {
      alert(err.message || "Failed to load passengers");
    }
  };

  const loadPassengerHistory = async (passengerId) => {
    try {
      const res = await api(`/admin/payments/passenger/${passengerId}/history`);
      setPassengerHistory(res);
    } catch (err) {
      alert(err.message || "Failed to load payment history");
      setPassengerHistory(null);
    }
  };

  useEffect(() => {
    loadPassengers();
  }, []);

  useEffect(() => {
    if (selectedPassengerId) {
      loadPassengerHistory(selectedPassengerId);
    } else {
      setPassengerHistory(null);
    }
  }, [selectedPassengerId]);

  const initializePayment = async (e) => {
    e.preventDefault();
    try {
      await api("/admin/payments/initialize", {
        method: "POST",
        body: createForm,
      });
      alert("Payment created successfully");
      setCreateForm({ passengerId: "", amount: "", dueDate: "" });
      loadPassengers();
      if (selectedPassengerId) {
        loadPassengerHistory(selectedPassengerId);
      }
    } catch (err) {
      alert(err.message || "Failed to create payment");
    }
  };

  const updatePaymentAmount = async (paymentId) => {
    if (!editAmount || parseFloat(editAmount) <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    
    try {
      await api(`/admin/payments/${paymentId}`, {
        method: "PUT",
        body: { amount: parseFloat(editAmount) },
      });
      setEditingPaymentId(null);
      setEditAmount("");
      if (selectedPassengerId) {
        loadPassengerHistory(selectedPassengerId);
      }
      loadPassengers();
    } catch (err) {
      alert(err.message || "Failed to update payment amount");
    }
  };

  const startEditingAmount = (payment) => {
    setEditingPaymentId(payment._id);
    setEditAmount(payment.amount.toString());
  };

  const cancelEditingAmount = () => {
    setEditingPaymentId(null);
    setEditAmount("");
  };

  const markAsPaid = async (paymentId) => {
    try {
      await api(`/admin/payments/${paymentId}/mark-paid`, {
        method: "POST",
      });
      if (selectedPassengerId) {
        loadPassengerHistory(selectedPassengerId);
      }
      loadPassengers();
    } catch (err) {
      alert(err.message || "Failed to mark as paid");
    }
  };

  const markAsPending = async (paymentId) => {
    try {
      await api(`/admin/payments/${paymentId}/mark-pending`, {
        method: "POST",
      });
      if (selectedPassengerId) {
        loadPassengerHistory(selectedPassengerId);
      }
      loadPassengers();
    } catch (err) {
      alert(err.message || "Failed to mark as pending");
    }
  };

  const createNextMonth = async (passengerId) => {
    const hasUnpaid = passengerHistory.payments.some(
      (p) => p.status === "pending" || p.status === "overdue"
    );
    
    if (hasUnpaid) {
      alert("Cannot create next month payment. There are unpaid payments.");
      return;
    }

    try {
      await api("/admin/payments/create-next-month", {
        method: "POST",
        body: { passengerId },
      });
      alert("Next month payment created");
      if (selectedPassengerId) {
        loadPassengerHistory(selectedPassengerId);
      }
      loadPassengers();
    } catch (err) {
      alert(err.message || "Failed to create next month payment");
    }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm("Are you sure you want to delete this payment record?")) {
      return;
    }

    try {
      await api(`/admin/payments/${paymentId}`, {
        method: "DELETE",
      });
      alert("Payment deleted successfully");
      if (selectedPassengerId) {
        loadPassengerHistory(selectedPassengerId);
      }
      loadPassengers();
    } catch (err) {
      alert(err.message || "Failed to delete payment");
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "paid":
        return "approved";
      case "pending":
        return "pending";
      case "overdue":
        return "rejected";
      default:
        return "";
    }
  };

  const bulkDeletePayments = async () => {
    if (bulkSelectedPayments.length === 0) {
      alert("No payments selected");
      return;
    }
    if (window.confirm(`Delete ${bulkSelectedPayments.length} payment(s)?`)) {
      try {
        await api("/admin/payments/bulk-delete", {
          method: "POST",
          body: { paymentIds: bulkSelectedPayments },
        });
        setBulkSelectedPayments([]);
        if (selectedPassengerId) {
          loadPassengerHistory(selectedPassengerId);
        }
        loadPassengers();
      } catch (err) {
        alert(err.message || "Failed to delete payments");
      }
    }
  };

  const bulkMarkPayments = async (status) => {
    if (bulkSelectedPayments.length === 0) {
      alert("No payments selected");
      return;
    }
    const action = status === 'paid' ? 'paid' : 'unpaid';
    if (window.confirm(`Mark ${bulkSelectedPayments.length} payment(s) as ${action}?`)) {
      try {
        await api("/admin/payments/bulk-mark", {
          method: "POST",
          body: { paymentIds: bulkSelectedPayments, status },
        });
        setBulkSelectedPayments([]);
        if (selectedPassengerId) {
          loadPassengerHistory(selectedPassengerId);
        }
        loadPassengers();
      } catch (err) {
        alert(err.message || "Failed to mark payments");
      }
    }
  };

  const togglePaymentSelection = (paymentId) => {
    setBulkSelectedPayments((prev) =>
      prev.includes(paymentId)
        ? prev.filter((id) => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const toggleSelectAll = () => {
    const allPaymentIds = passengerHistory?.payments.map((p) => p._id) || [];
    const allSelected = allPaymentIds.every((id) => bulkSelectedPayments.includes(id));

    if (allSelected) {
      setBulkSelectedPayments([]);
    } else {
      setBulkSelectedPayments(allPaymentIds);
    }
  };

  // Filter passengers based on search query
  const filteredPassengers = passengers.filter((p) => {
    const query = searchQuery.toLowerCase();
    return (
      p.name.toLowerCase().includes(query)
    );
  });

  const paginatedPassengers = filteredPassengers.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const totalPages = Math.ceil(filteredPassengers.length / itemsPerPage) || 1;

  return (
    <div className="card">
      <h3>Payments Management</h3>
      
      <div className="nested-tabs">
        <button
          className={`user-tab-btn${paymentTab === "overview" ? " active" : ""}`}
          onClick={() => {
            setPaymentTab("overview");
            setSelectedPassengerId(null);
          }}
        >
          Overview
        </button>
        <button
          className={`user-tab-btn${paymentTab === "initialize" ? " active" : ""}`}
          onClick={() => setPaymentTab("initialize")}
        >
          Create Payment
        </button>
      </div>

      {paymentTab === "overview" ? (
        <div>
          {selectedPassengerId && passengerHistory ? (
            <div>
              <button
                className="btn"
                style={{ marginBottom: 16 }}
                onClick={() => setSelectedPassengerId(null)}
              >
                ← Back to All Passengers
              </button>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "1.5rem",
                  marginBottom: "1.5rem",
                  background: "#f9fafb",
                }}
              >
                <h4 style={{ margin: 0, marginBottom: 8 }}>
                  {passengerHistory.passenger.name}
                </h4>
                <p style={{ margin: 0, fontSize: "0.875rem", color: "#6b7280" }}>
                  {passengerHistory.passenger.email} • {passengerHistory.passenger.contact || "No contact"}
                </p>
              </div>

              <div style={{ marginBottom: 16 }}>
                <button
                  className="btn success"
                  onClick={() => createNextMonth(selectedPassengerId)}
                >
                  Create Next Month Payment
                </button>
              </div>

              {bulkSelectedPayments.length > 0 && (
                <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, color: "#2d7a64" }}>
                    {bulkSelectedPayments.length} selected
                  </span>
                  <button
                    className="btn danger"
                    onClick={bulkDeletePayments}
                  >
                    Delete Selected
                  </button>
                  <button
                    className="btn success"
                    onClick={() => bulkMarkPayments('paid')}
                  >
                    Mark Paid
                  </button>
                  <button
                    className="btn warn"
                    onClick={() => bulkMarkPayments('pending')}
                  >
                    Mark Unpaid
                  </button>
                  <button
                    className="btn secondary"
                    onClick={() => setBulkSelectedPayments([])}
                  >
                    Clear Selection
                  </button>
                </div>
              )}

              <h4>Payment History</h4>
              <div className="scroll-x">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: "50px" }}>
                        <input
                          type="checkbox"
                          checked={
                            passengerHistory?.payments.length > 0 &&
                            passengerHistory.payments.every((p) => bulkSelectedPayments.includes(p._id))
                          }
                          onChange={toggleSelectAll}
                          style={{ cursor: "pointer" }}
                        />
                      </th>
                      <th>Month/Year</th>
                      <th>Amount (PKR)</th>
                      <th>Due Date</th>
                      <th>Paid Date</th>
                      <th>Status</th>
                      {bulkSelectedPayments.length === 0 && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {passengerHistory.payments.length === 0 && (
                      <tr>
                        <td colSpan={7}>No payment records</td>
                      </tr>
                    )}
                    {passengerHistory.payments.map((payment) => (
                      <tr key={payment._id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={bulkSelectedPayments.includes(payment._id)}
                            onChange={() => togglePaymentSelection(payment._id)}
                            style={{ cursor: "pointer" }}
                          />
                        </td>
                        <td>{payment.monthYear}</td>
                        <td>
                          {editingPaymentId === payment._id ? (
                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                              <input
                                type="number"
                                min="1"
                                step="0.01"
                                value={editAmount}
                                onChange={(e) => setEditAmount(e.target.value)}
                                style={{
                                  width: "100px",
                                  padding: "0.3rem",
                                  fontSize: "0.875rem",
                                }}
                              />
                              <button
                                className="btn success"
                                style={{ fontSize: "0.7rem", padding: "0.3rem 0.5rem" }}
                                onClick={() => updatePaymentAmount(payment._id)}
                              >
                                ✓
                              </button>
                              <button
                                className="btn"
                                style={{ fontSize: "0.7rem", padding: "0.3rem 0.5rem" }}
                                onClick={cancelEditingAmount}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                              <span>PKR {payment.amount}</span>
                              <button
                                className="btn"
                                style={{ fontSize: "0.7rem", padding: "0.2rem 0.4rem" }}
                                onClick={() => startEditingAmount(payment)}
                                title="Edit amount"
                              >
                                ✏️
                              </button>
                            </div>
                          )}
                        </td>
                        <td>{formatKarachiDate(payment.dueDate)}</td>
                        <td>{formatKarachiDate(payment.paidDate)}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(payment.status)}`}>
                            {payment.status}
                          </span>
                        </td>
                        {bulkSelectedPayments.length === 0 && (
                          <td>
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                              {payment.status === "paid" ? (
                                <button
                                  className="btn warn"
                                  style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem" }}
                                  onClick={() => markAsPending(payment._id)}
                                >
                                  Mark Pending
                                </button>
                              ) : (
                                <button
                                  className="btn success"
                                  style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem" }}
                                  onClick={() => markAsPaid(payment._id)}
                                >
                                  Mark Paid
                                </button>
                              )}
                              <button
                                className="btn"
                                style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem", background: "#dc2626", color: "white" }}
                                onClick={() => deletePayment(payment._id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div>
              <h4>All Passengers</h4>
              
              <div style={{ marginBottom: "1rem" }}>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    width: "100%",
                    maxWidth: "400px",
                    padding: "0.5rem",
                    fontSize: "0.875rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", alignItems: "center" }}>
                <button
                  className="btn"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Prev
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
                <select
                  className="tms-select"
                  value={itemsPerPage} 
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setPage(1);
                  }}
                  style={{ padding: "0.5rem", fontSize: "0.875rem" }}
                >
                  {[20, 50, 100].map((num) => (
                    <option key={num} value={num}>{num} per page</option>
                  ))}
                </select>
              </div>

              <div className="scroll-x">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Contact</th>
                      <th>Setup Status</th>
                      <th>Current Month</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPassengers.map((p) => (
                      <tr key={p._id}>
                        <td>{p.name}</td>
                        <td>{p.email}</td>
                        <td>{p.contact || "-"}</td>
                        <td>
                          <span className={`badge ${p.hasPaymentSetup ? "approved" : "pending"}`}>
                            {p.hasPaymentSetup ? "Setup" : "Not Setup"}
                          </span>
                        </td>
                        <td>
                          {p.currentMonthPayment ? (
                            <span className={`badge ${getStatusBadgeClass(p.currentMonthPayment.status)}`}>
                              {p.currentMonthPayment.status}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <button
                            className="btn"
                            style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem" }}
                            onClick={() => setSelectedPassengerId(p._id)}
                          >
                            View History
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : paymentTab === "initialize" ? (
        <div>
          <h4>Create Payment Record</h4>
          <form onSubmit={initializePayment} style={{ maxWidth: 600 }}>
            <label>Select Passenger</label>
            <select
              value={createForm.passengerId}
              onChange={(e) => setCreateForm((f) => ({ ...f, passengerId: e.target.value }))}
              required
            >
              <option value="">Select Passenger</option>
              {passengers.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name} ({p.email})
                </option>
              ))}
            </select>

            <label>Payment Amount (PKR)</label>
            <input
              type="number"
              min="1"
              step="0.01"
              value={createForm.amount}
              onChange={(e) => setCreateForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="Enter amount"
              required
            />

            <label>Due Date</label>
            <input
              type="date"
              value={createForm.dueDate}
              onChange={(e) => setCreateForm((f) => ({ ...f, dueDate: e.target.value }))}
              required
            />

            <button className="btn success" type="submit" style={{ marginTop: 16 }}>
              Create Payment
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default TabPayments;
